// [PURPOSE]: Synthesize generic `proficiency` grant effects from a custom race's
//   top-level proficiency label arrays, so custom-race proficiencies flow through
//   effectRegistry exactly like SRD races (which author them as featureEffects).
// [WHY]: EDITOR-RACE-prof-skill-bridge-FIX (1a). The CustomRaceCreator
//   Proficiencies→Skills grid stores `race.proficiencies.skills` as Title-case
//   labels that reached NO skill reader. Trace:
//   .claude/findings/2026-06-13-datamodel-race-prof-skillproficiency-bridge-rpb1.md.
//   Synthesizing at REGISTER-time (not author-time) fixes every existing saved
//   custom race with ZERO migration — storage stays the label array.
// [SHAPE]: mirrors SRD (src/data/srdRaceFeatures.js:76):
//   { type:'proficiency', operation:'grant', value:'skill:<key>', proficiencyLevel:1 }
// [SCOPE]: SKILLS ONLY — the verified reader path (effectCalculator.calculateSkillBonus
//   does `Math.max(dictLevel, registryLevel)` via getGrantedProficiencyLevel, so no
//   double-count). Tools/weapons/armor read via a DIFFERENT direct path
//   (SkillsPanel.js:517 `proficiencies.tools.includes()`), NOT the registry, so
//   routing them here would not reach their readers — queued as 1b with the required
//   reader-analysis. [BREADCRUMB 1b]: do NOT emit weapon:/armor:/tool: grants until
//   their registry readers are confirmed. See master-queue EDITOR-RACE-prof-skill-bridge-FIX.

import { SKILL_NAME_TO_KEY } from './skillProficiencies';

/**
 * Convert a custom race's proficiency label arrays into generic effectRegistry
 * `proficiency` grant effects. Pure — returns a new array, no side effects.
 *
 * @param {Object|null} proficiencies - e.g. { skills: ['Acrobatics'], tools: [...] }
 * @returns {Array} grant effects, e.g.
 *   [{ type:'proficiency', operation:'grant', value:'skill:acrobatics', proficiencyLevel:1 }]
 */
export function synthesizeRaceProficiencyEffects(proficiencies) {
  if (!proficiencies || typeof proficiencies !== 'object') return [];
  const effects = [];

  const skills = Array.isArray(proficiencies.skills) ? proficiencies.skills : [];
  skills.forEach((label) => {
    const key = SKILL_NAME_TO_KEY[label];
    if (key) {
      effects.push({ type: 'proficiency', operation: 'grant', value: `skill:${key}`, proficiencyLevel: 1 });
    }
    // Unmapped labels are silently skipped (same policy as convertSkillNamesToObject).
  });

  // [BREADCRUMB 1b]: weapons/armor/tools intentionally NOT synthesized yet — their
  //   readers don't consult the registry. Emitting grants here would be an unverified
  //   "fix" that reaches no reader.

  return effects;
}

export default synthesizeRaceProficiencyEffects;
